import React, { FC, useMemo } from 'react';

import { FormattedMessage, useIntl } from 'react-intl';

import { ReactTable, ShortCodeEnums } from '@nhl/nhl-breakaway-ui';

import commonMessages from '~/core/commonMessages';
import { PlayerStatNames } from '~/core/enums';

import { ReactTableContainer } from '../../styles';
import { CommonGCGame, PeriodValue } from '../../types';

import { createColumns } from './columns';

import { HeaderContainer, ShotsOnGoalContainer } from './styles';

type Props = {
    game: CommonGCGame;
    shotsByPeriod?: Array<PeriodValue>;
};

type ColumnPeriod = PeriodValue & {
    isFuturePeriod?: boolean;
    isTotal?: boolean;
};

const ShotsOnGoalByPeriod: FC<Props> = ({ game, shotsByPeriod }) => {
    let awayTotal = 0;
    let homeTotal = 0;
    const intl = useIntl();

    const columns = useMemo(() => createColumns(game, intl), [game, intl]);

    shotsByPeriod?.forEach((period) => {
        awayTotal += period.away || 0;
        homeTotal += period.home || 0;
    });

    const formatShotsPerPeriod = (): Array<ColumnPeriod> => {
        const formattedShotsByPeriod: Array<ColumnPeriod> = [];

        // first, iterate over and insert all game regulation periods
        const regulationPeriods: Array<number> = Array(game.regPeriods)
            .fill(0)
            .map((x, i) => i + 1);

        regulationPeriods.forEach((periodNumber, index) => {
            const regulationPeriodDescriptor = {
                maxRegulationPeriods: game.regPeriods,
                number: periodNumber,
                periodType: ShortCodeEnums.PeriodType.regulation,
            };

            const periodShots = shotsByPeriod?.[index];
            if (periodShots) {
                formattedShotsByPeriod.push(periodShots);
            } else {
                // future regulation periods
                formattedShotsByPeriod.push({
                    away: 0,
                    home: 0,
                    periodDescriptor: regulationPeriodDescriptor,
                    isFuturePeriod: true,
                });
            }
        });

        // next, insert any OT periods (excluding SO)
        shotsByPeriod
            ?.slice(game.regPeriods, shotsByPeriod.length)
            .filter(({ periodDescriptor }) => periodDescriptor.periodType === ShortCodeEnums.PeriodType.overtime)
            .forEach((periodShots) => {
                formattedShotsByPeriod.push(periodShots);
            });

        //lastly, add in the totals row
        formattedShotsByPeriod.push({
            away: awayTotal,
            home: homeTotal,
            periodDescriptor: {
                maxRegulationPeriods: game.regPeriods,
                number: 0,
                periodType: ShortCodeEnums.PeriodType.regulation,
            },
            isTotal: true,
        });

        return formattedShotsByPeriod;
    };

    return shotsByPeriod?.length ? (
        <ShotsOnGoalContainer>
            <HeaderContainer role="heading" aria-level={2}>
                <FormattedMessage {...commonMessages.statsDescriptions.long[PlayerStatNames.shotsOnGoal]} />
            </HeaderContainer>
            <ReactTableContainer>
                <ReactTable data={formatShotsPerPeriod()} columns={columns} />
            </ReactTableContainer>
        </ShotsOnGoalContainer>
    ) : (
        <></>
    );
};

export default ShotsOnGoalByPeriod;
